import * as React from "react";
import { useUser, useOrganization } from "@clerk/clerk-react";
import { useQuery, useMutation, useAction } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Pane1AgentsChannels } from "./Pane1AgentsChannels";
import { Pane2CollaborationThread } from "./Pane2CollaborationThread";
import { Pane3SidekickCanvas } from "./Pane3SidekickCanvas";
import { PaneAgentsOverview } from "./PaneAgentsOverview";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import { SEO } from "@/components/SEO";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PanelRightClose, PanelRightOpen, MessageSquare, Briefcase, Repeat, TrendingUp } from "lucide-react";
import { UpgradePromptDialog } from "@/components/billing/UpgradePromptDialog";
import { AllWorkModal } from "./AllWorkModal";
import { AllRoutinesModal } from "./AllRoutinesModal";
import { ProductivitySpendModal } from "./ProductivitySpendModal";
import { AgentUsageSheet } from "./AgentUsageSheet";

export default function AgentModePage() {
  const { user } = useUser();
  const { organization } = useOrganization();

  // Find user's Convex ID if available
  const currentUser = useQuery(api.users.getCurrentUser, user ? {} : "skip");
  const orgId = currentUser?.orgId;
  const personalWorkspaceId = currentUser?.personalWorkspaceId;

  // Mutations & Actions
  const ensureChannels = useMutation(api.aiWorkforce.threads.ensureDefaultChannels);
  const getOrCreateDM = useMutation(api.aiWorkforce.threads.getOrCreateAgentDM);
  const runTurn = useAction(api.aiWorkforce.orchestrator.runWorkforceTurn);
  const approveAction = useMutation(api.aiWorkforce.approvals.approveAction);
  const rejectAction = useMutation(api.aiWorkforce.approvals.rejectAction);
  const approveArtifactDirectly = useMutation(api.aiWorkforce.approvals.approveArtifactDirectly);

  // Subscriptions
  const channels = useQuery(
    api.aiWorkforce.threads.listChannelsAndDMs,
    currentUser ? { orgId, personalWorkspaceId } : "skip"
  );

  const pendingApprovals = useQuery(
    api.aiWorkforce.approvals.listPendingApprovals,
    currentUser ? { orgId, personalWorkspaceId } : "skip"
  );

  const [activeView, setActiveView] = React.useState<"chat" | "agents">("chat");
  const [activeThreadId, setActiveThreadId] = React.useState<string | null>(null);
  const [activeArtifactId, setActiveArtifactId] = React.useState<string | null>(null);
  const [isLoadingTurn, setIsLoadingTurn] = React.useState(false);
  const [isSidekickCanvasOpen, setIsSidekickCanvasOpen] = React.useState(true);
  const [isAllWorkOpen, setIsAllWorkOpen] = React.useState(false);
  const [isAllRoutinesOpen, setIsAllRoutinesOpen] = React.useState(false);
  const [isProductivityOpen, setIsProductivityOpen] = React.useState(false);
  const [selectedUsageAgentKey, setSelectedUsageAgentKey] = React.useState<string | null>(null);

  const subscription = useQuery(api.subscriptions.getUserSubscription, user ? {} : "skip");
  const usage = useQuery(
    api.usage.getUsage,
    user
      ? {
          orgId: orgId as any,
          personalWorkspaceId: personalWorkspaceId as any,
        }
      : "skip"
  );

  const purchasedCredits = (usage?.purchasedCredits ?? (subscription as any)?.purchasedCredits ?? 0);
  const hasPurchasedCredits = purchasedCredits > 0;
  const monthlyRemaining = usage ? Math.max(0, usage.creditLimit - usage.creditsUsed) : 0;
  const hasActivePaidPlan = Boolean(
    subscription && subscription.status === "active" && (subscription.amount || 0) > 0
  );
  // Only block if user has no top-up credits AND no active plan with remaining monthly credits
  const isCreditBlocked =
    (subscription !== undefined || usage !== undefined) &&
    !hasPurchasedCredits &&
    (!hasActivePaidPlan || monthlyRemaining <= 0);

  const [showUpgradeModal, setShowUpgradeModal] = React.useState(false);

  // Initialize default channels on mount
  React.useEffect(() => {
    if (currentUser) {
      ensureChannels({ orgId, personalWorkspaceId }).catch(console.error);
    }
  }, [currentUser, ensureChannels, orgId, personalWorkspaceId]);

  // Set default active thread once loaded
  React.useEffect(() => {
    if (channels && channels.length > 0 && !activeThreadId) {
      const defaultChan = channels.find((c: any) => c.type === "channel") || channels[0];
      setActiveThreadId(defaultChan._id);
    }
  }, [channels, activeThreadId]);

  // Active thread details & messages
  const activeThread = useQuery(
    api.aiWorkforce.threads.getThread,
    activeThreadId ? { threadId: activeThreadId as any } : "skip"
  );

  const messages = useQuery(
    api.aiWorkforce.threads.listMessages,
    activeThreadId ? { threadId: activeThreadId as any } : "skip"
  );

  // Sync active work output from thread or selection
  const activeArtifact = useQuery(
    api.aiWorkforce.artifacts.getArtifact,
    (activeArtifactId || activeThread?.activeArtifactId)
      ? { id: (activeArtifactId || activeThread?.activeArtifactId) as any }
      : "skip"
  );

  // List all work items for the current channel / thread
  const channelArtifacts = useQuery(
    api.aiWorkforce.artifacts.listArtifactsByThread,
    activeThreadId ? { threadId: activeThreadId as any } : "skip"
  );

  // Full approval history and audit trail
  const approvalHistory = useQuery(
    api.aiWorkforce.approvals.listApprovalHistory,
    currentUser ? { orgId, personalWorkspaceId } : "skip"
  );

  const handleSendMessage = async (content: string) => {
    if (!activeThreadId) return;
    if (isCreditBlocked) {
      setShowUpgradeModal(true);
      return;
    }
    setIsLoadingTurn(true);
    const clientNonce = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `turn-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    try {
      const result = await runTurn({
        threadId: activeThreadId as any,
        userPrompt: content,
        clientNonce,
        orgId,
        personalWorkspaceId,
        userId: currentUser?._id,
      });

      if (result?.artifactId) {
        setActiveArtifactId(result.artifactId);
        setIsSidekickCanvasOpen(true);
      }
    } catch (err: any) {
      console.error("[Agent Mode] Request error:", err);
    } finally {
      setIsLoadingTurn(false);
    }
  };

  const handleApprove = async (approvalId: string) => {
    try {
      const res = await approveAction({
        approvalId: approvalId as any,
        userId: currentUser?._id,
      });
      const insertedCount = (res as any)?.result?.insertedLeads;
      if (insertedCount) {
        toast.success(`${insertedCount} leads saved to your pipeline!`);
      } else {
        toast.success("Action approved successfully.");
      }
    } catch (err: any) {
      toast.error("Failed to approve action: " + err.message);
    }
  };

  const handleApproveArtifactDirectly = async (artifactId: string) => {
    try {
      const res = await approveArtifactDirectly({
        artifactId: artifactId as any,
        userId: currentUser?._id,
      });
      const insertedCount = (res as any)?.result?.insertedLeads;
      if (insertedCount) {
        toast.success(`${insertedCount} leads saved to your pipeline!`);
      } else {
        toast.success("Artifact approved and changes saved.");
      }
    } catch (err: any) {
      toast.error("Failed to approve artifact: " + err.message);
    }
  };

  const handleReject = async (approvalId: string, reason?: string) => {
    try {
      await rejectAction({
        approvalId: approvalId as any,
        userId: currentUser?._id,
        reason,
      });
      toast.info("Action declined.");
    } catch (err: any) {
      toast.error("Failed to decline action: " + err.message);
    }
  };

  const handleSelectAgentDM = async (personaKey: string) => {
    setActiveView("chat");
    try {
      const threadId = await getOrCreateDM({
        orgId,
        personalWorkspaceId,
        personaKey,
      });
      if (threadId) {
        setActiveThreadId(threadId);
        setActiveArtifactId(null);
        setIsSidekickCanvasOpen(true);
      }
    } catch (err: any) {
      toast.error("Failed to open chat: " + err.message);
    }
  };

  const isCurrentDM = activeThread?.type === "dm";

  return (
    <DashboardLayout activeTab="agent-mode" showAgentSidebar={false}>
      <UpgradePromptDialog
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title="Upgrade to Starter"
        description="Access your AI Business Assistant to get expert advice, automate tasks, and draft documents instantly."
        feature="AI Business Assistant"
        planName="Starter"
        planFeatures={["Unlimited Bids & Proposals", "Connect apps like Gmail & Notion", "Advanced Analytics"]}
      />
      <SEO
        title="Agent Workforce Operations | ManageClean OS"
        description="Assign tasks, review generated work, and collaborate with your commercial cleaning operations team."
      />

      {/* Main Viewport Container locked to 100% height without scrolling the sidebar or window */}
      <div className="w-full h-[calc(100vh-7.5rem)] max-h-[calc(100vh-7.5rem)] overflow-hidden flex flex-col">
        {/* Page Title & Status Header */}
        <div className="flex items-center justify-between pb-4 flex-shrink-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
              Agent Workforce Operations
            </h1>
          </div>
          <div className="flex items-center gap-2.5">
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => setIsAllWorkOpen(true)}
            >
              <Briefcase className="h-4 w-4" />
              <span>All Work</span>
            </Button>

            <Button
              variant="outline"
              className="gap-2"
              onClick={() => setIsAllRoutinesOpen(true)}
            >
              <Repeat className="h-4 w-4" />
              <span>All Routines</span>
            </Button>

            <Button
              variant="outline"
              className="gap-2 text-indigo-700 bg-indigo-50/60 hover:bg-indigo-100/80 border-indigo-200 shadow-2xs font-semibold"
              onClick={() => setIsProductivityOpen(true)}
            >
              <TrendingUp className="h-4 w-4 text-indigo-600" />
              <span>Productivity & Spend</span>
            </Button>

            {activeView === "agents" ? (
              <Button
                variant="outline"
                onClick={() => setActiveView("chat")}
                className="gap-2 border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800 shadow-xs"
              >
                <MessageSquare className="w-4 h-4 text-blue-600" />
                <span>Return to Channels</span>
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => setIsSidekickCanvasOpen((prev) => !prev)}
                className={`gap-2 shadow-xs ${
                  isSidekickCanvasOpen
                    ? "bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                    : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                }`}
              >
                {isSidekickCanvasOpen ? (
                  <>
                    <PanelRightClose className="w-4 h-4 text-blue-600" />
                    <span>Hide Panel</span>
                  </>
                ) : (
                  <>
                    <PanelRightOpen className="w-4 h-4 text-gray-500" />
                    <span>Show Details</span>
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Adaptive CSS Grid Layout */}
        <div
          className={`w-full flex-1 min-h-0 min-w-0 grid gap-4 overflow-hidden ${
            activeView === "agents"
              ? "grid-cols-1 lg:grid-cols-[280px_1fr] 2xl:grid-cols-[300px_1fr]"
              : isSidekickCanvasOpen
                ? "grid-cols-1 lg:grid-cols-[280px_1fr] xl:grid-cols-[280px_1fr_360px] 2xl:grid-cols-[300px_1fr_400px]"
                : "grid-cols-1 lg:grid-cols-[280px_1fr] 2xl:grid-cols-[300px_1fr]"
          }`}
        >
          {/* Pane 1: Directory & Channels */}
          <Pane1AgentsChannels
            channels={channels?.filter((c: any) => c.type !== "dm") || []}
            activeThreadId={activeThreadId || undefined}
            activeThread={activeThread}
            activeView={activeView}
            onSelectView={setActiveView}
            onSelectThread={(id) => {
              setActiveThreadId(id);
              setActiveArtifactId(null);
            }}
            onSelectAgentDM={handleSelectAgentDM}
            pendingApprovalsCount={pendingApprovals?.length || 0}
          />

          {/* Pane 2: Either Agents Overview OR Live Collaboration Thread */}
          {activeView === "agents" ? (
            <PaneAgentsOverview
              onSelectPersonaChannel={handleSelectAgentDM}
              onOpenAgentUsage={(key) => setSelectedUsageAgentKey(key)}
              orgId={orgId}
              personalWorkspaceId={personalWorkspaceId}
            />
          ) : (
            <Pane2CollaborationThread
              activeThread={activeThread}
              messages={messages || []}
              onSendMessage={handleSendMessage}
              onSelectArtifact={(id) => {
                setActiveArtifactId(id);
                setIsSidekickCanvasOpen(true);
              }}
              onApprove={handleApprove}
              onReject={handleReject}
              isLoadingTurn={isLoadingTurn}
            />
          )}

          {/* Pane 3: Sidekick Canvas for Details, Work, and Approvals */}
          {activeView === "chat" && isSidekickCanvasOpen && (
            <Pane3SidekickCanvas
              channel={activeThread}
              channelArtifacts={channelArtifacts || []}
              activeArtifact={activeArtifact}
              onSelectArtifact={(id) => setActiveArtifactId(id)}
              pendingApprovals={pendingApprovals || []}
              approvalHistory={approvalHistory || []}
              onApprove={handleApprove}
              onReject={handleReject}
              onApproveArtifact={handleApproveArtifactDirectly}
              onClose={() => setIsSidekickCanvasOpen(false)}
            />
          )}
        </div>
      </div>

      {/* All Work Modal */}
      <AllWorkModal
        isOpen={isAllWorkOpen}
        onClose={() => setIsAllWorkOpen(false)}
        orgId={orgId}
        personalWorkspaceId={personalWorkspaceId}
        onSelectArtifact={(artifactId, threadId) => {
          setActiveView("chat");
          setActiveThreadId(threadId);
          setActiveArtifactId(artifactId);
          setIsSidekickCanvasOpen(true);
        }}
      />

      {/* All Routines Modal */}
      <AllRoutinesModal
        isOpen={isAllRoutinesOpen}
        onClose={() => setIsAllRoutinesOpen(false)}
        onOpenAgentChat={async (personaKey) => {
          try {
            const dmThreadId = await getOrCreateDM({
              orgId,
              personalWorkspaceId,
              personaKey,
            });
            if (dmThreadId) {
              setActiveView("chat");
              setActiveThreadId(dmThreadId);
            }
          } catch (err: any) {
            toast.error("Could not open chat with agent: " + err.message);
          }
        }}
      />

      {/* Workforce Productivity, Spend & ROI Modal */}
      <ProductivitySpendModal
        isOpen={isProductivityOpen}
        onClose={() => setIsProductivityOpen(false)}
        orgId={orgId}
        personalWorkspaceId={personalWorkspaceId}
        onSelectAgentUsage={(key) => {
          setIsProductivityOpen(false);
          setSelectedUsageAgentKey(key);
        }}
      />

      {/* Agent Granular Usage & ROI Sheet */}
      <AgentUsageSheet
        isOpen={Boolean(selectedUsageAgentKey)}
        onClose={() => setSelectedUsageAgentKey(null)}
        personaKey={selectedUsageAgentKey}
        orgId={orgId}
        personalWorkspaceId={personalWorkspaceId}
      />
    </DashboardLayout>
  );
}
